import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { api } from '../services/api';
import './FormStyles.css';
import { FaSearch } from 'react-icons/fa';
import StepDrawer from './StepDrawer';

function SurgeonSelection({
  states,
  onContinue,
  currentStep,
  totalSteps,
  onBack,
  onCancel,
  formData,
  setFormData,
  customState,
  setCustomState,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const observer = useRef();

  const [currentlySearchedState, setCurrentlySearchedState] = useState('')

  useLayoutEffect(() => {
    const el = document.querySelector('.App.scrollable');
    if (el) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      console.warn('Element not found');
    }
  }, [])

  const lastResultElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const handleCustomStateChange = (e => {
    setCustomState(e.target.value)
  })

  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        let stateToSearch = customState ? customState : formData.state.name
        const formattedState = stateToSearch.replace(/\s+/g, '_').toLowerCase();
        setCurrentlySearchedState(formattedState)
        const data = await api.searchDoctors(formattedState, searchQuery, 1, 10);
        if (data && data.doctors) {
          setSearchResults(data.doctors);
          setHasMore(data.hasMore || false);
          setPage(1);
        }
      } catch (err) {
        console.error('Error searching doctors:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    setDebounceTimer(timer);

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [searchQuery, formData.state, customState]);

  useEffect(() => {
    if (page === 1) return;

    const loadMoreResults = async () => {
      try {
        setLoading(true);
        let stateToSearch = customState ? customState : formData.state.name
        const formattedState = stateToSearch.replace(/\s+/g, '_').toLowerCase();
        setCurrentlySearchedState(formattedState)
        const data = await api.searchDoctors(formattedState, searchQuery, page, 10);
        if (data && data.doctors) {
          setSearchResults(prevResults => [...prevResults, ...data.doctors]);
          setHasMore(data.hasMore || false);
        }
      } catch (err) {
        console.error('Error loading more results:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMoreResults();
  }, [page, formData.state, searchQuery]);

  const toggleDescription = (i) => {
    searchResults[i].showDescription = !searchResults[i].showDescription
    setSearchResults([...searchResults])
  }

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };


  const handleSelectDoctor = (doctor) => {
    const actualState = customState ? customState : formData.state.name
    const surgeon = {
      npi: doctor.npi,
      name: doctor.name,
      address: doctor.address,
      state: { name: actualState }
    }
    setFormData({ ...formData, surgeon: { ...surgeon } })
    setSearchQuery('');
    setSearchResults([]);
    onContinue()
  };


  const handleClearSelection = () => {
    setFormData({ ...formData, surgeon: null })
  };

  return (
    <StepDrawer
      currentStep={currentStep}
      totalSteps={totalSteps}
      onCancel={onCancel}
      onContinue={onContinue}
      title="Who performed your surgery?"
      onBack={onBack}
      showSkip={!formData.surgeon}
    >
      <div className="form-content" style={{ paddingBottom: '0px', margin: '0px', minHeight: '200px' }}>
        {formData.surgeon ? (
          <div className="selected-surgeon-container">
            <span className="selected-label">Selected:</span>
            <div className="selected-surgeon-pill">
              <span>{formData.surgeon.name}</span>
              <button className="clear-selection" onClick={handleClearSelection}>×</button>
            </div>
          </div>
        ) : (
          <>
            <p className="selection-hint" style={{ color: 'rgb(156, 163, 175)'}} >Prefer not to say?</p>
            <div onClick={onContinue} style={{ color: '#3498db', cursor: 'pointer', paddingBottom: '1rem' }}>Skip this step</div>
          </>
        )}
        <div className="search-input-container">
          <FaSearch className="search-icon" />
          <input
            id="doctor-search"
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
            style={{ paddingLeft: '36px', appearance: 'none' }}
          />
        </div>

        {(searchResults.length > 0 || loading) && (
          <div
            className="search-results"
            style={{ flex: 1, marginTop: '0px', border: 'none', boxShadow: 'none' }}
          >
            {searchResults.map((doctor, index) => {
              if (formData.surgeon && formData.surgeon.npi === doctor.npi) {
                return <React.Fragment key={doctor.npi} />;
              }

              return (
                <div
                  className="line-item"
                  key={doctor.npi}
                  ref={index === searchResults.length - 1 ? lastResultElementRef : null}
                >
                  <div className="line-item-header">
                    <div className="doctor-name">{doctor.name}</div>

                    {doctor.showDescription ? (
                      <div style={{ color: 'rgb(156, 163, 175)', display: 'flex', flexDirection: 'column' }}>
                        <div>{doctor.address}</div>
                      </div>
                    ) : (
                      <div className="line-item-description-button" onClick={() => toggleDescription(index)}>
                        More Information
                      </div>
                    )}
                  </div>
                  <div className="line-item-button" onClick={() => handleSelectDoctor(doctor)}>
                    Click To Select
                  </div>
                </div>
              );
            })}
            {loading && <div className="loading">Searching...</div>}
            {!loading && !hasMore &&
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                className="loading">
                <div>
                  No more results in {currentlySearchedState == "*" ? "All States" : currentlySearchedState}
                </div>
                {searchQuery &&
                  <>
                    <button
                      style={{
                        marginTop: '0px',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        color: '#3498db'
                      }}
                      onClick={() => setSearchQuery('')}
                      className="add-new-button">Clear search phrase</button>
                    <div>or</div>
                  </>
                }
                <select
                  value={customState || ''}
                  onChange={handleCustomStateChange}
                  className="search-input"
                  style={{ color: '#666', maxWidth: '400px' }}
                >
                  <option value="" disabled>Search in another state...{customState}</option>
                  <option key="all" value="*">
                    All States
                  </option>
                  {states.map((state) => (
                    <option key={state.name} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>}
          </div>
        )}
        {(!searchResults.length && !loading) && (
          <div className="search-results" style={{ flex: 1, marginTop: '0px', border: 'none', boxShadow: 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} className="loading">
              <div>
                No results in {currentlySearchedState == "*" ? "All States" : currentlySearchedState}
              </div>
              {searchQuery &&
                <>
                  <button
                    style={{
                      marginTop: '0px',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      color: '#3498db'
                    }}
                    onClick={() => setSearchQuery('')}
                    className="add-new-button">Clear serch phrase</button>
                  <div>or</div>
                </>
              }
              <select
                value={customState || ''}
                onChange={handleCustomStateChange}
                className="search-input"
                style={{ color: '#666', maxWidth: '400px' }}
              >
                <option value="">Search in another state...{customState}</option>
                <option key="all" value="*">
                  All States
                </option>
                {states.map((state) => (
                  <option key={state.name} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </StepDrawer>
  );
}

export default SurgeonSelection; 